
import { Component, Vue, Watch } from 'vue-property-decorator';
import 'vue-select/dist/vue-select.css';
import Rooms from '@/components/home/Rooms.vue';
import Popup from '@/components/utils/Popup.vue';
import { DataService } from '@/service/DataService';
import Spaces from '@/components/home/Spaces.vue';
import LogoHorizontal from '@/components/utils/LogoHorizontal.vue';
import MediaIcon from '@/components/utils/MediaIcon.vue';
import MessagePopup from '@/components/popup/common/MessagePopup.vue';
import AddRoomFromHistoryPopup from '@/components/popup/home/AddRoomFromHistoryPopup.vue';
import GeneralSettingsPopup from '@/components/popup/common/GeneralSettingsPopup.vue';
import {
  Room,
  RoomSpace,
  SettingsSnapshot,
  User,
  UserRoomState,
  SiteInfo,
  UserRoomStateData,
  DropdownOption,
  RoomCount,
  TrashBoxSpaceId,
  UserProperties,
  GeneralSettingsSnapshot,
  zawaroom,
  InvitedSpaceIdAndRoomId,
} from '@/utils/models';
import LruCache from 'lru-cache';
import { getDataServerUrl, isAdmin } from '@/utils/functions';
import LoadingPopup from '@/components/popup/room/LoadingPopup.vue';
import StatusIcon from '@/components/room/users/StatusIcon.vue';
import QuestionnairePopup from '@/components/popup/home/QuestionnairePopup.vue';

@Component({
  components: {
    GeneralSettingsPopup,
    AddRoomFromHistoryPopup,
    MessagePopup,
    MediaIcon,
    LogoHorizontal,
    Spaces,
    Popup,
    Rooms,
    LoadingPopup,
    StatusIcon,
    UserRoomStateData,
    QuestionnairePopup,
  },
})
export default class HomeView extends Vue {
  dataService: DataService | null = null; // Vue 2 の場合は null を入れとかないとダメでした。。。
  cacheRoomStates: LruCache<string, UserRoomState[]> = new LruCache<string, UserRoomState[]>({ max: 500 });
  roomCount: RoomCount = {};

  isLoading: boolean = false;
  isQuestionnairePopupVisible = false;
  spacesState: RoomSpace[] = [];
  myFavoriteRooms: Set<string> = new Set<string>();

  private get user(): User | undefined {
    return this.$store.state?.user;
  }

  private get userId(): string {
    return this.user?.id || '';
  }

  get selectedSpace(): RoomSpace | undefined {
    return this.spacesState.find((s) => s.id == this.selectedSpaceId);
  }

  get selectedRoom(): Room | undefined {
    return this.selectedRoomId ? this.selectedSpace?.rooms?.find((r) => r.id == this.selectedRoomId) : undefined;
  }

  get isAdmin(): boolean {
    return !!this.user && isAdmin(this.user);
  }
  get hasTrash(): boolean {
    const user = this.user;
    if (!user) {
      return false;
    }
    if (this.isAdmin) {
      return true;
    }
    return !!this.spacesState
      .find((s) => s.spaceType === 'trash')
      ?.rooms?.find((r) => !!r.adminEmails?.find((e) => e === user.email));
  }
  private selectedSpaceId: string = '';
  private selectedRoomId: string = '';

  isGeneralSettingsPopupVisible = false;
  isMessagePopupVisible = false;

  messagePopupMessage = '';
  messagePopupTitle = '';

  menuCategory: 'favorites' | 'all' | 'bySpace' = 'bySpace';

  mounted() {
    console.log('home view mounted');
    this.dataService = this.$store.state.dataService;
    this.setValuesAccordingToStore();
    this.addDataServerMessagesListeners();
  }
  beforeDestroy() {
    this.dataService!.removeAllListeners();
    this.cacheRoomStates.forEach((v, k) => this.cacheRoomStates.del(k));
    console.log('home view destroy');
  }

  private onClearRoomStateCache(roomId: string | 'reset') {
    console.log(`delete cache → roomId: ${roomId}\nroomData: ${this.cacheRoomStates.get(`${roomId}`)}`);
    this.cacheRoomStates.del(`${roomId}`);
  }

  private addDataServerMessagesListeners(): void {
    this.dataService!.onceConnected(() => {
      this.dataService!.emitRegisterOnWelcomePage();
      this.dataService!.emitGetMyFavoriteRooms();
    });
    this.dataService!.onUpdateUserProps((user: Partial<User>) => {
      this.onUpdateUserProps(user);
    });
    this.dataService!.onRemoveError((message: string, type: 'room' | 'space') => this.onRemoveError(message, type));
    this.dataService!.onSpacesStateChanged((spacesState: RoomSpace[]) => this.onSpacesStateChanged(spacesState));
    this.dataService!.onClearRoomStateCache((roomId: string | 'reset') => this.onClearRoomStateCache(roomId));
    this.dataService!.onDuplicateSpaceRoom((roomOrSpace: 'room' | 'space') => this.onDuplicateSpaceRoom(roomOrSpace));
    this.dataService!.onChangeMyFavoriteRooms((roomIds: string[]) => {
      this.myFavoriteRooms = new Set<string>(roomIds);
    });
    this.dataService!.onUpdateRoomCount((rc) => (this.roomCount = rc));
  }

  private onDuplicateSpaceRoom(roomOrSpace: 'room' | 'space') {
    const title = roomOrSpace === 'room' ? 'ルーム' : 'スペース';
    this.$toast.error('ゲスト許可のない' + title + '名と重複しています。');
  }
  private onUpdateUserProps(user: Partial<User>): void {
    if (user.id == this.userId) {
      const props = (Object.keys(user) as (keyof User)[]).filter((p) => p !== 'id');
      if (props.length) {
        const u: User = this.$store.state.user;
        props.forEach((p) => this.$set(u, p, user[p]));
      }
    }
  }
  onApplyNewSettings(gs: GeneralSettingsSnapshot) {
    this.isGeneralSettingsPopupVisible = false;
    this.$store.commit('SetSettings', gs);
  }
  onCloseSetting() {
    this.isGeneralSettingsPopupVisible = false;
  }

  private onRemoveError(message: string, type: 'room' | 'space'): void {
    if (type === 'room') {
      this.messagePopupTitle = 'ルーム削除エラー';
      this.messagePopupMessage = message;
    } else if (type === 'space') {
      this.messagePopupTitle = 'スペース削除エラー';
      this.messagePopupMessage = message;
    }
    this.openMessagePopup();
  }

  private onSpacesStateChanged(spacesState: RoomSpace[]): void {
    this.spacesState = spacesState;
  }

  private setValuesAccordingToStore(): void {
    const properties: UserProperties = this.$store.state.userProperties;
    this.selectedSpaceId = properties.lastSelectedSpaceId;
    this.selectedRoomId = properties.lastSelectedRoomId;
    this.menuCategory = properties.lastSelectedMenuCategory;
  }
  join(room: Room): void {
    if (room) {
      this.selectedSpaceId = room.spaceId;
      this.selectedRoomId = room.id;
    }
    this.saveSelectedInfo().then(() => {
      this.dataService!.emitLeaveFromWelcomePage();
      this.$router.push({
        path: `/room/${this.selectedSpaceId}/${this.selectedRoomId}`,
      });
    });
  }
  gotoAdmin() {
    this.$router.push({
      path: `/admin`,
    });
  }

  private saveSelectedInfo(): Promise<UserProperties> {
    return this.dataService!.emitUpdateUserProperties({
      userId: this.userId,
      lastSelectedMenuCategory: this.menuCategory,
      lastSelectedRoomId: this.selectedRoomId,
      lastSelectedSpaceId: this.selectedSpaceId,
    });
  }

  openGlobalSettings(): void {
    this.isGeneralSettingsPopupVisible = true;
  }

  private openMessagePopup(): void {
    this.isMessagePopupVisible = true;
  }
  hideMessagePopup(): void {
    this.isMessagePopupVisible = false;
  }

  requestRoomLogs(request: { roomId: string; range: Date[] }) {
    this.isLoading = true;
    this.dataService!.emitRoomGetLog(request.roomId, request.range).then((path) => {
      const servUrl = getDataServerUrl();
      const url = servUrl + path.path;
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = path.filename;
      a.href = url;
      a.click();
      a.remove();
      this.isLoading = false;
    });
  }
  public setTrashBox() {
    this.setSelectedSpace(TrashBoxSpaceId);
    this.menuCategory = 'bySpace';
  }

  public setSelectedSpace(space: RoomSpace | null | undefined | string): void {
    if (typeof space === 'string') {
      this.selectedSpaceId = space;
    } else {
      this.selectedSpaceId = space?.id ?? '';
    }
  }

  get shouldShowSiteLink(): boolean {
    return this.$store.state.siteInfo?.showSiteLink?.includes('yes');
  }
  get setSiteName(): string {
    return this.$store.state.siteInfo?.siteLinkName;
  }

  get isTrialSite(): boolean {
    return !!this.$store.state.siteInfo?.isDemo;
  }

  get userAuthType(): string {
    return this.$store.state.user?.authType || '';
  }

  get anotherSiteName(): string {
    if (this.shouldShowSiteLink && this.setSiteName) {
      return this.setSiteName;
    } else if (this.shouldShowSiteLink) {
      return 'zawaroomサポートサイト';
    } else {
      return '';
    }
  }
  get setSiteUrl(): string {
    return this.$store.state.siteInfo?.siteLinkUrl;
  }
  get anotherSiteUrl(): string {
    if (this.shouldShowSiteLink && this.setSiteUrl) {
      return this.setSiteUrl;
    } else if (this.shouldShowSiteLink) {
      return 'https://www.zawaroom.com/support';
    } else {
      return '';
    }
  }
  goAnotherSite() {
    window.open(this.anotherSiteUrl, '_blank', 'noreferrer');
  }

  navigateToInvitedRoom() {
    if (!this.isTrialSite) return;
    const invitation: InvitedSpaceIdAndRoomId | undefined = this.$store.state.invitation;
    if (invitation) {
      this.$router.push(`/room/${invitation[0]}/${invitation[1]}`);
      this.$store.commit('setInvitation', undefined);
    }
  }

  onQuestionnaireFinish() {
    this.isQuestionnairePopupVisible = false;
    const updatedUserProperties = {
      ...this.$store.state.userProperties,
      demoQuestionnarieAnswered: true,
    };
    this.$store.commit('SetUserProperties', updatedUserProperties);
    this.navigateToInvitedRoom();
  }

  created() {
    if (this.isTrialSite) {
      if (!this.$store.state.userProperties.demoQuestionnarieAnswered) {
        //アンケート未回答であればアンケートポップアップ表示
        this.isQuestionnairePopupVisible = true;
      } else {
        //アンケート回答済みの場合、ルームに招待されていたらそちらに遷移
        this.navigateToInvitedRoom();
      }
    }
  }
}
